import { Box, Grid, MenuItem, Paper, Select, SelectProps } from '@material-ui/core';
import { Currency, LenderName } from '@nemuru-eng/js-api-client';

import { DateTimeIntervals } from '@/components/modules/Funnel/types/DateTimeIntervals';
import { DocumentationErrorIcon } from '@/components/shared/components/ErrorIcon/components/DocumentationErrorIcon';
import { LoanApplicationErrorIcon } from '@/components/shared/components/ErrorIcon/components/LoanApplicationErrorIcon';
import { Icon } from '@/components/shared/components/Icon';
import { LenderSelectFilter } from '@/components/shared/components/LenderSelectFilter/LenderSelectFilter';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { ApplicationType } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { useUser } from '@/hooks/useUser';

interface FunnelFiltersProps {
  onChangeProduct: (name: string, input: ApplicationType) => void;
  productFilter: number | ApplicationType;
  onChangeDateInterval: (name: string, input: number) => void;
  dateFilter: number;
  onChangeLender: (lenderName: LenderName) => void;
  lenderNameFilter: LenderName[];
  onChangeMinAmount: (minAmount: number) => void;
  currency: Currency;
  onChangeMaxAmount: (maxAmount: number) => void;
  incidenceFilter: boolean;
  onChangeIncidencesFilter: () => void;
  errorsFilter: boolean;
  onChangeErrorsFilter: () => void;
}

export const FunnelFilters = (props: FunnelFiltersProps) => {
  const { palette } = useCustomTheme();
  const { userEntity } = useUser();
  const { sm } = useScreen();

  if (!userEntity) {
    return null;
  }

  const NON_VISIBLE_PRODUCTS_IN_FILTER = [
    ApplicationType.A2A_PAYMENT,
    ApplicationType.CARD_PAYMENT,
    ApplicationType.CHECKOUT_SESSION,
  ];

  const ProductFilterSelector = ({ selectProps }: { selectProps?: SelectProps }) => {
    const allowedProducts = userEntity.allowedProducts.filter(
      (product) => !NON_VISIBLE_PRODUCTS_IN_FILTER.includes(product),
    );

    return (
      <Box pt={1}>
        <Select onChange={props.onChangeProduct} value={props.productFilter} {...selectProps}>
          {allowedProducts.length > 1 && (
            <MenuItem value={0}>
              <Text tId={'ALL_PRODUCTS'} />
            </MenuItem>
          )}
          {allowedProducts.map((value) => (
            <MenuItem value={value} key={`menuItem-${value}`}>
              <Text tId={`${value}_LIST`} />
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  const DateSelector = ({ selectProps }: { selectProps?: SelectProps }) => {
    return (
      <Box pt={1}>
        <Select onChange={props.onChangeDateInterval} value={props.dateFilter} {...selectProps}>
          {DateTimeIntervals.map((interval) => (
            <MenuItem value={interval.minDateDiff} key={`dateMenuItem-${interval.tId}`}>
              <Text tId={interval.tId} />
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  const Filters = () => {
    return (
      <Box display={'flex'} gridGap={sm ? '8px' : '14px'} flexDirection={sm ? 'row' : 'column'}>
        <Box>
          <StandardButton
            variant={props.incidenceFilter ? 'contained' : 'text'}
            size={'small'}
            color="secondary"
            onClick={props.onChangeIncidencesFilter}
            startIcon={<Icon size={20}>{props.incidenceFilter ? 'check_circle' : 'circle'}</Icon>}
            style={{ padding: '8px 8px' }}
          >
            <span style={{ marginRight: '8px' }}>
              <Text tId={'WITH_INCIDENCE'} />
            </span>
            <DocumentationErrorIcon
              iconColor={props.incidenceFilter ? palette.secondary.contrastText : palette.secondary.main}
            />
          </StandardButton>
        </Box>
        {!userEntity?.isRoleMerchantChannelOrAgent() && (
          <Box>
            <StandardButton
              variant={props.errorsFilter ? 'contained' : 'text'}
              size={'small'}
              color="secondary"
              onClick={props.onChangeErrorsFilter}
              startIcon={<Icon size={20}>{props.errorsFilter ? 'check_circle' : 'circle'}</Icon>}
              style={{ padding: '8px 8px' }}
            >
              <span style={{ marginRight: '8px' }}>
                <Text tId={'WITH_ERROR'} />
              </span>
              <LoanApplicationErrorIcon
                iconColor={props.errorsFilter ? palette.secondary.contrastText : palette.secondary.main}
              />
            </StandardButton>
          </Box>
        )}
      </Box>
    );
  };

  if (!sm) {
    return (
      <Grid item xs={12}>
        <Box display={'flex'} gridGap={'12px'} flexDirection={'column'} maxWidth={'100%'} overflow={'hidden'}>
          <ProductFilterSelector selectProps={{ style: { width: '100%' } }} />
          <LenderSelectFilter selectedLenders={props.lenderNameFilter} setSelectedLenders={props.onChangeLender} />
          <DateSelector selectProps={{ style: { width: '100%' } }} />
          <Box mt={2}>
            <Filters />
          </Box>
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Box mt={-3}>
        <Paper elevation={0} style={{ backgroundColor: 'inherit' }}>
          <Box display={'flex'} gridGap={'24px'} alignItems={'end'} justifyContent={'space-between'} flexWrap={'wrap'}>
            <Box display={'flex'} gridGap={'24px'}>
              <ProductFilterSelector />
              <LenderSelectFilter selectedLenders={props.lenderNameFilter} setSelectedLenders={props.onChangeLender} />
              <DateSelector />
            </Box>
            <Filters />
          </Box>
        </Paper>
      </Box>
    </Grid>
  );
};
